// The renderer's view of the desktop shell (TODO §14.4.7).
//
// `electron/preload.ts` exposes exactly three globals over `contextBridge`; this module is
// the only place in the web bundle that names them, so the shell's surface is described once
// and every consumer gets a typed, guarded accessor instead of an `as any` on `window`.
//
// Everything here answers `null` on the website. That is not a degraded path to be handled
// specially — it is the normal one: the same bundle serves both runtimes (§14.4.5), so every
// caller must already work without a shell.

/** §14.3.6 The base URLs, which are only known once the shell has bound its ports. */
export interface DesktopConfig {
  griddEmbedBaseUrl: string;
  griddLibraryBaseUrl: string;
}

/** A `.deckai` the user picked in the native Open dialog (§14.7.3). */
export interface OpenedDeckFile {
  name: string;
  data: Uint8Array;
}

/** Which documents are open, and which is in front — persisted in `userData` (§14.7.6). */
export interface OpenTabsState {
  deckIds: string[];
  activeDeckId: string | null;
}

/**
 * §15/§5.2.2 What the main process got for a chart's data URL. A discriminated result rather
 * than a thrown error, because it crosses `contextBridge`: an `Error` does not survive the
 * structured clone with its message intact, and the message is the whole point — main knows
 * the status code, the byte count and whether it timed out, and the page does not.
 */
export type RemoteTextResult =
  | { ok: true; text: string; status: number; contentType: string | null }
  | { ok: false; error: string };

export interface LocalModelHead {
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

export interface DesktopShell {
  appVersion: string;
  /** The Gridd app build the shell serves offline (§14.1.6) — for About / deck stamping. */
  embeddableVersion: string | null;
  platform: string;
  /** §14.5.3 Is this a URL the shell will proxy to the user's own machine? */
  isLocalModelUrl(url: string): boolean;
  startLocalModelRequest(
    req: {
      url: string;
      method: string;
      headers: Record<string, string>;
      body: string | Uint8Array | null;
    },
    handlers: {
      onHead: (head: LocalModelHead) => void;
      onChunk: (chunk: Uint8Array) => void;
      onEnd: () => void;
      onError: (message: string) => void;
    },
  ): () => void;
  /**
   * §15/§5.2.2 Fetch a chart's remote CSV/JSON from the main process, which has no CORS policy
   * to answer to — the only transport for which "any URL works" is true (`src/lib/chartFetch.ts`).
   *
   * Buffered, not streamed, unlike `startLocalModelRequest`: a table is read whole before it can
   * be parsed at all, so a stream would only be reassembled on this side. The caps are passed in
   * rather than fixed in the shell so the page's own limits are the ones that apply, and main
   * clamps them to its own maxima regardless — a renderer must not be able to raise them.
   *
   * OPTIONAL on the interface: a shell built before this existed exposes no such function, and
   * `chartFetch` falls back to the direct transport rather than crashing.
   */
  fetchRemoteText?(
    url: string,
    opts: { timeoutMs: number; maxBytes: number },
  ): Promise<RemoteTextResult>;
  getOpenTabs(): Promise<{ deckIds: string[]; activeDeckId: string | null }>;
  setOpenTabs(state: OpenTabsState): void;
  openWindow(startPath: string): void;
  openDeckFile(): Promise<OpenedDeckFile | null>;
  saveFile(suggestedName: string, data: Uint8Array): Promise<boolean>;
  /**
   * §6 Render this window's print layer to PDF bytes, on a page the size of one slide. The
   * desktop shell has no print preview, so `window.print()` would print onto the system's
   * portrait paper and ignore the `@page` rule the web build relies on; main sets the page
   * box explicitly instead. Caller-supplied dimensions are CSS px (SLIDE_W × SLIDE_H).
   */
  printToPdf(widthPx: number, heightPx: number): Promise<Uint8Array>;
  onMenuCommand(handler: (command: string) => void): () => void;
}

declare global {
  interface Window {
    __slidecraftDesktop?: boolean;
    __slidecraftConfig?: DesktopConfig;
    __slidecraftShell?: DesktopShell;
  }
}

/** The shell bridge, or `null` on the website (and in tests). */
export function desktopShell(): DesktopShell | null {
  if (typeof window === "undefined") return null;
  return window.__slidecraftShell ?? null;
}

/** §14.3.6 The launch-time config, or `null` when the build-time values should win. */
export function desktopConfig(): DesktopConfig | null {
  if (typeof window === "undefined") return null;
  const config = window.__slidecraftConfig;
  if (!config || typeof config !== "object") return null;
  return config;
}

/** The native menu (§14.7.4) and the in-app buttons dispatch the same set of commands. */
export type DesktopMenuCommand =
  | "new-deck"
  | "open-deck"
  | "save-a-copy"
  | "close-tab"
  | "next-tab"
  | "previous-tab"
  | "open-in-new-window"
  | "documents";

const MENU_COMMANDS = new Set<string>([
  "new-deck",
  "open-deck",
  "save-a-copy",
  "close-tab",
  "next-tab",
  "previous-tab",
  "open-in-new-window",
  "documents",
]);

export function isDesktopMenuCommand(
  value: string,
): value is DesktopMenuCommand {
  return MENU_COMMANDS.has(value);
}
